import { SpDropdownToggle } from 'components/shared/Dropdown';
import { FormattedMessage } from 'components/shared/Intl';
import { IrreversibleConfirmModal } from 'components/shared/Modals/IrreversibleConfirmModal';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useToggle } from 'hooks/useToggle';
import { FC, useCallback } from 'react';
import { Dropdown, DropdownDivider, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import type { WorkspaceType } from 'state/workspaces/model';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';
import styled from 'styled-components';

import { MenuItem } from './MenuItem';

export interface MoreMenuProps {
  workspaceType: WorkspaceType;
  addNewTab?: () => void;
  importExcel?: () => void;
  clearAll?: () => void;
  tabs: WorkspaceTab[];
  onTabActivated?: (tab: WorkspaceTab) => void;
}

const StyledDiv = styled.div`
  max-height: 75vh;
  overflow-y: auto;
`;

export const MoreMenu: FC<MoreMenuProps> = ({
  addNewTab,
  tabs,
  clearAll,
  onTabActivated,
  workspaceType,
  importExcel,
}) => {
  const { formatMessage } = useIntl();
  const isInternalUser = useIsInternalUser();
  const { isOn: isDropdownOpened, toggle: toggleDropdown } = useToggle();
  const { isOn: isModalOpened, toggleOff: closeModal, toggleOn: openModal } = useToggle();

  const onConfirmClearAll = useCallback(() => {
    closeModal();
    clearAll?.();
  }, [clearAll, closeModal]);

  return (
    <Dropdown data-e2e="tabs-dropdown" show={isDropdownOpened} onToggle={toggleDropdown}>
      <SpDropdownToggle
        id={`more-menu-toggle-${workspaceType}`}
        caret={false}
        variant="link"
        className="btn-icon px-0"
        color="flat-secondary"
        size="lg"
        aria-label={formatMessage({ id: 'moreMenu.toggleLabel' })}
        aria-haspopup="menu"
        aria-expanded={isDropdownOpened}
      >
        <i className="icon" aria-hidden="true">
          more_vert
        </i>
      </SpDropdownToggle>
      <DropdownMenu role="menu" aria-labelledby={`more-menu-toggle-${workspaceType}`}>
        {addNewTab && (
          <DropdownItem role="menuitem" data-e2e="addNewTab" onClick={addNewTab}>
            <FormattedMessage id="addWorkspaceTab" />
            <i className="icon icon-sm ps-2" aria-hidden="true">
              add
            </i>
          </DropdownItem>
        )}
        {isInternalUser && importExcel && (
          <DropdownItem role="menuitem" data-e2e="import-excel" onClick={importExcel}>
            <FormattedMessage id="importFromExcel" />
            <i className="icon icon-sm ps-2" aria-hidden="true">
              exit_to_app
            </i>
          </DropdownItem>
        )}
        {clearAll && (
          <DropdownItem role="menuitem" onClick={openModal} data-e2e="clear-all-tabs">
            <FormattedMessage id="clearAll" />
          </DropdownItem>
        )}
        <DropdownDivider />
        <StyledDiv role="group" aria-label={formatMessage({ id: 'moreMenu.tabsListLabel' })}>
          {tabs.map(tab => (
            <MenuItem key={tab.id} tab={tab} onTabActivated={onTabActivated} workspaceType={workspaceType} />
          ))}
        </StyledDiv>
      </DropdownMenu>
      <IrreversibleConfirmModal
        onConfirm={onConfirmClearAll}
        isOpen={isModalOpened}
        onClosed={closeModal}
        title={<FormattedMessage id="clearAllTabs" />}
        proceedMessage={<FormattedMessage id="proceedMessage" />}
        description={<FormattedMessage id="tabs.warningMessage" />}
      />
    </Dropdown>
  );
};
